import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { Platform } from 'react-native';
import { getUserData } from '../utils/helpers';
import { AxiosError } from 'axios';

console.log('Loading Sentry config...');

// Función para determinar en qué ambiente estamos
export const getEnvironment = () => {
  try {
    if (Platform.OS === 'web') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'development';
      }
      if (hostname.includes('dev')) {
        return 'staging';
      }
      return 'production';
    } else {
      return 'production';
    }
  } catch (error) {
    console.error('Error getting environment:', error);
    return 'development';
  }
};

const getDsn = () => {
  try {
    const env = getEnvironment();

    const dsnConfig = {
      development:
        'https://c95e2a14cde304dca10c85ae670c8eb4@o4507465517891584.ingest.us.sentry.io/4507470976712704',
      staging:
        'https://c95e2a14cde304dca10c85ae670c8eb4@o4507465517891584.ingest.us.sentry.io/4507470976712704',
      production:
        'https://c95e2a14cde304dca10c85ae670c8eb4@o4507465517891584.ingest.us.sentry.io/4507470976712704',
    };

    return dsnConfig[env] || dsnConfig.production;
  } catch (error) {
    console.error('Error getting DSN:', error);
    return 'https://c95e2a14cde304dca10c85ae670c8eb4@o4507465517891584.ingest.us.sentry.io/4507470976712704';
  }
};

export const initSentry = () => {
  const env = getEnvironment();
  if (env === 'development') {
    console.log('Development environment - not sending event to Sentry');
    return;
  }
  try {
    console.log('Initializing Sentry...');
    const environment = getEnvironment();
    const dsn = getDsn();

    if (!dsn) {
      console.error('No DSN provided for Sentry');
      return;
    }

    Sentry.init({
      dsn,
      environment,
      tracesSampleRate: environment === 'production' ? 0.5 : 1.0,
      integrations: [
        new BrowserTracing({
          tracePropagationTargets: [
            'localhost',
            'pos.mozzolife.com',
            'api.mozzolife.com',
            /^\//,
          ],
        }),
      ],
      release: '1.0.0',
     async beforeSend(event) {
        if (environment === 'development') {
           console.log('Development environment - not sending event to Sentry');
           return null;
        }
        const user = await getUserData();
        if (user?._id) {
          event.user = {
            ...event.user,
            id: user._id,
            email: user.email,
            role: user.role,
            selectedAccount: user.selectedAccount,
            clientId: user.settings?.clientId,
          };
        }
        return event;
      },
    });

    Sentry.setTag('app_version', '1.0.0');
    Sentry.setTag('platform', Platform.OS);
  } catch (error) {
    console.error('Error initializing Sentry:', error);
  }
};

export const captureAxiosError = async (
  error: AxiosError
): Promise<void> => {
  const { response, config } = error;
  const user = await getUserData();

  Sentry.withScope(async (scope) => {
    scope.setLevel('error');
    if (user?._id) {
      scope.setUser({
        id: user._id,
        email: user.email || undefined,
        role: user.role || undefined,
        selectedAccount: user.selectedAccount || undefined,
        clientId: user.settings?.clientId || undefined,
      });
      scope.setTag('mozzo_user.id', user._id);
      scope.setTag('mozzo_user.email', user.email || undefined);
      scope.setTag('mozzo_user.role', user.role || undefined);
      scope.setTag('mozzo_user.selected_account', user.selectedAccount || undefined);
      scope.setTag('mozzo_user.client_id', user.settings?.clientId || undefined);
    } else {
      scope.setUser({ id: null });
    }

    if (config) {
      scope.setTag('request.method', config.method?.toUpperCase() || 'UNKNOWN');
      scope.setTag('request.url', config.url || 'unknown');
      scope.setExtra('request.headers', config.headers);

      if (config.data && ['POST', 'PUT', 'PATCH'].includes(config.method?.toUpperCase() || '')) {
        try {
          const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
          scope.setExtra('request.data', requestData);
        } catch (e) {
          scope.setExtra('request.data', config.data);
        }
      }

      if (config.params) {
        scope.setExtra('request.params', config.params);
      }
    }

    if (response) {
      scope.setTag('response.status', response.status.toString());
      scope.setExtra('response.data', response.data);
      scope.setExtra('response.headers', response.headers);
    } else {
      scope.setTag("response.status", "NO_RESPONSE");
      scope.setExtra("network.error", {
        message: error.message,
        code: error.code,
        request: error.request?._response || null,
      });
    }
    Sentry.captureException(error);
  });
};